import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import styles from './ClinicPatientFormResponse.module.scss';
import { Row, Col, Card } from 'react-bootstrap';
import Loader from '../components/Loader';
import PatientHeader from '../components/PatientHeader';
import { alertModal } from '../utils/modalController';
import FormDiagnosis from '../components/FormDiagnosis';
import { fetchAuth } from '../utils/fetchAuth';
import { generateFileUrl } from '../utils/fileUrl';

const apiUrl = process.env.REACT_APP_API_URL || '';

const ClinicPatientFormResponse: React.FC = () => {

    const [user, setUser] = useState<User | null>(null);
    const [age, setAge] = useState<number | null>(null);
    const [formResponse, setFormResponse] = useState<FormResponseFields | null>(null);
    const [score, setScore] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    const { id } = useParams<{ id: string }>();
    const { formId } = useParams<{ formId: string }>();
    const [searchParams] = useSearchParams();
    const consultaId = searchParams.get('consulta');
    const responseId = searchParams.get('response') || null;
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            fetchAuth(apiUrl + `/consultorio/form-responses/${formId}?n_response=${responseId ?? 1}`),
            fetchAuth(`${apiUrl}/consultorio/patients/${id}`),
        ])
            .then(async ([formsRes, patientRes]) => {
                if (!formsRes.ok || !patientRes.ok) {
                    throw new Error('Erro ao buscar dados');
                }

                const formsJson = await formsRes.json();
                const patientJson = await patientRes.json();

                if (!patientJson.user) {
                    throw new Error("Usuário não encontrado");
                }

                const userRes = await fetchAuth(`${apiUrl}/consultorio/users/${patientJson.user.id}`);
                if (!userRes.ok) {
                    const error_json = await userRes.json();
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const userJson = await userRes.json();

                setUser(userJson);
                setAge(userJson?.birthday ? Math.floor((new Date().getTime() - new Date(userJson.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
                setFormResponse(formsJson);
                setScore((formsJson as FormResponseFields | null)?.fields?.reduce((acc, field) => {
                    if (!field.options?.length) return acc;
                    // Soma os scores das opções selecionadas
                    const fieldScore = field.options
                        .reduce((sum, opt) => sum + (opt.score ?? 0), 0);
                    // Aplica o limite se existir
                    const limitedScore =
                        field.max_score != null ? Math.min(fieldScore, field.max_score) : fieldScore;
                    return acc + limitedScore;
                }, 0) ?? null);
            })
            .catch(async (err) => {
                console.error('Erro ao buscar dados:', err);
                await alertModal('Erro ao buscar dados: ' + err);
                setUser(null);
                setFormResponse(null);
                setAge(null);
                navigate(`/consultorio/pacientes/${id}/formularios`, { replace: true });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, formId]);

    const handleClick = async (fileUrl: string) => {
        try {
            const res = await fetchAuth(fileUrl);

            if (!res.ok) {
                const errorJson = await res.json();
                throw new Error(`${errorJson.message}`);
            }

            // pega filename do header
            const disposition = res.headers.get("Content-Disposition") || "";
            let fileName = "download";

            const match = disposition.match(/filename="(.+)"/);
            if (match && match[1]) {
                fileName = decodeURIComponent(match[1]);
            }

            // cria url temporária
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);

            // força o download com nome correto
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error("Erro ao baixar arquivo:", err);
            alertModal("Erro ao baixar arquivo.");
        }
    };


    if (loading) return <Loader />;

    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Row className={styles["row-card"]}>
                            <Col>
                                <PatientHeader
                                    user={user!}
                                    age={age}
                                    breadcrumbItems={[
                                        { label: "Consultório", href: "/consultorio" },
                                        { label: "Pacientes", href: "/consultorio/pacientes" },
                                        { label: user?.name || "Paciente", href: `/consultorio/pacientes/${id}` },
                                        ...(!consultaId
                                            ? [
                                                { label: "Formulários", href: `/consultorio/pacientes/${id}/formularios` },
                                            ]
                                            : [
                                                { label: "Consultas", href: `/consultorio/pacientes/${id}/consultas` },
                                                { label: "Consulta", href: `/consultorio/pacientes/${id}/consultas/${consultaId}` },
                                            ]),
                                        { label: formResponse?.title || "Formulário" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            <Container className="my-4">
                <Row className="data-content align-items-center">
                    <Col>
                        <Card className={styles["boas-vindas"]}>
                            <Card.Header className={styles["center"]}>
                                <Card.Title className={styles.h1}>{formResponse?.title ?? 'Formulário'}</Card.Title>
                                <p>Respondido em: {formResponse?.answered_at ? new Date(formResponse?.answered_at).toLocaleDateString('pt-BR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                }) : '--    -'}</p>
                            </Card.Header>
                            <Card.Body>
                                {(score !== 0 || formResponse?.classification_of_score) && (<Row className={styles['row-card']}>
                                    <Col>
                                        <FormDiagnosis
                                            score={score ?? 0}
                                            title={formResponse?.title ?? ''}
                                            classification_of_score={formResponse?.classification_of_score}
                                        />
                                    </Col>
                                </Row>)}
                                {formResponse?.fields?.map((item, index) => (
                                    <Row key={index} className={styles['row-card']}>
                                        <Col>
                                            <p>{index + 1}. {item?.label ?? ''}</p>
                                            {item?.image ? (<img src={item?.image ?? ''} alt={item?.label ?? ''} className={styles['img-form']} />) : (<></>)}
                                            {item?.value === '' && !(item?.options?.length ?? 0 > 0) ? (<p>Não respondida</p>) : (<></>)}
                                            {item?.value ? (<p><b>Resposta:</b> {
                                                item?.type === 'date' || item?.type === 'datetime' ? (item?.value && typeof item.value === 'string' && item?.value !== '' ? new Date(item.value).toLocaleDateString('pt-BR', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    hour: item?.type === 'datetime' ? '2-digit' : undefined,
                                                    minute: item?.type === 'datetime' ? '2-digit' : undefined,
                                                    second: item?.type === 'datetime' ? '2-digit' : undefined,
                                                }) : '---') :
                                                    (item?.value ?? '')
                                            }</p>) : (<></>)}
                                            {item?.options ? (<p><b>{item?.options?.length > 1 ? 'Opções selecionadas' : 'Opção selecionada'}:</b><br /> {item?.options?.map((option, index) => (
                                                <span key={index}>
                                                    {item?.type !== 'select-color' ? (
                                                        <>
                                                            {option.label}
                                                            {option?.image && option?.label !== '' && (<br />)}
                                                            {option?.image && (
                                                                <img
                                                                    src={option.image}
                                                                    alt={option.label ?? ''}
                                                                    className={styles['img-form']}
                                                                />
                                                            )}
                                                            {index < (item?.options?.length ?? 0) - 1 && <br />}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div
                                                                className={styles['color-box']}
                                                                style={{ backgroundColor: option.label }}
                                                            ></div>
                                                            {index < (item?.options?.length ?? 0) - 1 && <br />}
                                                        </>
                                                    )}
                                                </span>
                                            ))}</p>) : (<></>)}
                                            {item?.file ? (
                                                <p>
                                                    <b>Arquivo enviado:</b> <br />
                                                    <button type="button" onClick={() => handleClick(`${apiUrl}/consultorio/form-responses/files/${item.file}`)} style={{ background: 'none', border: 'none', padding: 0, color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}>
                                                        {'Clique aqui para baixar o arquivo'}
                                                    </button>
                                                </p>
                                            ) : (<></>)}
                                        </Col>
                                    </Row>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ClinicPatientFormResponse;
